/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import FeaturesMap from 'self-service-api/constants/FeaturesMap';
import ProductFeaturesMap from 'self-service-api/constants/ProductFeaturesMap';
import { currentOrderedPaidProductLevels, currentOrderedProductLevels, ENTERPRISE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { getProductFeatureDescriptionKey } from 'self-service-api/core/utilities/getFeatureDescriptionKey';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import H1 from 'UIComponents/elements/headings/H1';
import H6 from 'UIComponents/elements/headings/H6';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UITable from 'UIComponents/table/UITable';
import UILink from 'UIComponents/link/UILink';
import { SLINKY } from 'HubStyleTokens/colors';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import { isMon606InAppRunningForPublicUserSelector, isMon606VariantSelector } from '../../selectors/ExperimentTreatmentSelectors';
import { getMon606TextKeyBase } from '../../utils/mon606Utils';

const FeatureTable = ({
  title,
  subtitle,
  body
}) => {
  return /*#__PURE__*/_jsxs(UITable, {
    className: "m-top-6",
    children: [/*#__PURE__*/_jsx("thead", {
      children: /*#__PURE__*/_jsx("tr", {
        children: /*#__PURE__*/_jsxs("th", {
          colSpan: 2,
          className: "p-y-4",
          children: [/*#__PURE__*/_jsx(H6, {
            className: "m-bottom-2",
            children: title
          }), /*#__PURE__*/_jsx("span", {
            style: {
              fontSize: '12px',
              textTransform: 'initial',
              fontWeight: 'normal',
              color: SLINKY
            },
            children: subtitle
          })]
        })
      })
    }), /*#__PURE__*/_jsx("tbody", {
      children: body
    })]
  });
};

const PaidUsersModal = (_ref) => {
  let {
    productLevel,
    productLine,
    onCloseClick,
    onShowFullFeaturesModal
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["productLevel", "productLine", "onCloseClick", "onShowFullFeaturesModal"]);

  const titleCopyMap = {
    [SALES]: {
      [STARTER]: 'pricingPage.paidUsersModal.title.SALES.STARTER',
      [PROFESSIONAL]: 'pricingPage.paidUsersModal.title.SALES.PROFESSIONAL',
      [ENTERPRISE]: 'pricingPage.paidUsersModal.title.SALES.ENTERPRISE'
    },
    [SERVICE]: {
      [STARTER]: 'pricingPage.paidUsersModal.title.SERVICE.STARTER',
      [PROFESSIONAL]: 'pricingPage.paidUsersModal.title.SERVICE.PROFESSIONAL',
      [ENTERPRISE]: 'pricingPage.paidUsersModal.title.SERVICE.ENTERPRISE'
    }
  };

  const groupArrayIntoTwos = array => array.reduce((acc, value, index, fullArray) => {
    if (index % 2 === 0) acc.push(fullArray.slice(index, index + 2));
    return acc;
  }, []);

  const featureFactory = isPaid => {
    const orderedProductLevelsToUse = isPaid ? currentOrderedPaidProductLevels : currentOrderedProductLevels;
    const lowerTierProductLevel = orderedProductLevelsToUse.indexOf(productLevel) > 0 ? orderedProductLevelsToUse[orderedProductLevelsToUse.indexOf(productLevel) - 1] : null;
    const featuresToExclude = lowerTierProductLevel && !isPaid ? ProductFeaturesMap[productLine][lowerTierProductLevel] : {};
    const copyKeys = Object.keys(ProductFeaturesMap[productLine][productLevel]).filter(featureId => !(featureId in featuresToExclude) && (isPaid ? FeaturesMap[featureId] && FeaturesMap[featureId].freeUser : !FeaturesMap[featureId] || !FeaturesMap[featureId].freeUser));
    return groupArrayIntoTwos(copyKeys);
  };

  const paidFeatureCopyKeys = useMemo(() => featureFactory(true), [productLine, productLevel]);
  const freeFeatureCopyKeys = useMemo(() => featureFactory(false), [productLine, productLevel]);

  const makeFeatureCells = copyKeys => copyKeys.map(keys => /*#__PURE__*/_jsxs("tr", {
    children: [keys.map((key, index) => /*#__PURE__*/_jsx("td", {
      style: index === 0 ? {
        borderRight: `1px solid ${GREAT_WHITE}`
      } : {},
      children: /*#__PURE__*/_jsx(UITooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getProductFeatureDescriptionKey(key, productLine)
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          style: {
            cursor: 'help'
          },
          message: `pricing-pages.features.${key}.name`
        })
      })
    }, key)), keys.length === 1 && /*#__PURE__*/_jsx("td", {})]
  }, keys[0]));

  const isMon606Variant = useSelector(isMon606VariantSelector);
  const isMon606InAppRunningForPublicUser = useSelector(isMon606InAppRunningForPublicUserSelector);
  const showMon606VariantText = isMon606Variant || isMon606InAppRunningForPublicUser;
  const Mon606TextKeyBase = getMon606TextKeyBase(showMon606VariantText);
  return /*#__PURE__*/_jsxs(UIModal, Object.assign({
    width: 600,
    use: "conversational"
  }, rest, {
    children: [/*#__PURE__*/_jsx(UIDialogHeader, {
      children: /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onCloseClick
      })
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      className: "flex-column justify-center",
      children: [/*#__PURE__*/_jsx(H6, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: titleCopyMap[productLine][productLevel]
        })
      }), /*#__PURE__*/_jsx(H1, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.paidUsersModal.howManyTitle"
        })
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.paidUsersModal.primaryDifferenceSubtext"
      }), /*#__PURE__*/_jsx(FeatureTable, {
        className: "m-top-6",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.paidUsersModal.table.paidUsersOnlyTitle"
        }),
        subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.paidUsersModal.table.paidUsersOnlySubtitle"
        }),
        body: makeFeatureCells(paidFeatureCopyKeys)
      }), /*#__PURE__*/_jsx(FeatureTable, {
        className: "m-top-4",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${Mon606TextKeyBase}.paidUsersModal.table.freeUsersTitle`
        }),
        subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${Mon606TextKeyBase}.paidUsersModal.table.freeUsersSubtitle`
        }),
        body: makeFeatureCells(freeFeatureCopyKeys)
      }), /*#__PURE__*/_jsx(FormattedReactMessage, {
        className: "m-top-2",
        message: "pricingPage.paidUsersModal.lookingForMore",
        options: {
          product: /*#__PURE__*/_jsx(FormattedMessage, {
            message: titleCopyMap[productLine][productLevel]
          })
        }
      }), /*#__PURE__*/_jsx(UILink, {
        className: "m-top-1",
        onClick: onShowFullFeaturesModal,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.paidUsersModal.compareFeatures"
        })
      })]
    })]
  }));
};

export default PaidUsersModal;